import { Icon, type IconColor } from "@pepperstone/pepper-ui-kit";

import LoadingSpinner from "../../general/loading-spinner/LoadingSpinner";
import ProgressBar from "../../general/progress-bar/ProgressBar";
import type { UploadedFile } from "../BankStatementUpload";
import { UploadStatus } from "../enums/uploadStatus";

type BankStatementFileCardProps = {
  file: UploadedFile;
  progress: number;
  status: UploadStatus;
  errorMessage?: string;
  successMessage?: string;
  testId?: string;
  onDelete: (fileId: string) => void;
};

export const BankStatementFileCard = ({
  file,
  progress,
  status,
  errorMessage,
  successMessage,
  testId,
  onDelete,
}: BankStatementFileCardProps) => {
  const getIconByStatus = () => {
    switch (status) {
      case UploadStatus.PENDING:
      case UploadStatus.UPLOADING:
        return <LoadingSpinner size="sm" />;
      case UploadStatus.SUCCESS:
        return <Icon size={24} color="success" icon="check-mark-circle" />;
      case UploadStatus.ERROR:
        return <Icon size={24} color="error" icon="alert-triangle" />;
      default:
        return null;
    }
  };

  const getIconColorByStatus = (): IconColor => {
    switch (status) {
      case UploadStatus.PENDING:
      case UploadStatus.UPLOADING:
        return "secondary";
      case UploadStatus.SUCCESS:
        return "black";
      case UploadStatus.ERROR:
        return "error";
      default:
        return "error";
    }
  };

  const color =
    status === UploadStatus.ERROR
      ? "var(--color-icon-negative-default)"
      : "var(--color-icon-positive-default)";

  return (
    <div
      data-testid={testId}
      className="ps-flex ps-flex-row ps-items-center ps-gap-2 ps-mt-4 ps-mb-4"
    >
      {getIconByStatus()}
      <div className="ps-flex ps-flex-col ps-flex-1 ps-gap-1">
        <p className="ps-text-body-v2-sm">{file.name}</p>
        <ProgressBar
          color={color}
          height={3}
          progress={status === UploadStatus.ERROR ? 100 : progress}
        />
        {status === UploadStatus.ERROR && (
          <small
            data-testid={`${testId}-error`}
            className="ps-text-body-v2-xs"
            style={{ color: color }}
          >
            {errorMessage}
          </small>
        )}
        {status === UploadStatus.SUCCESS && (
          <small
            data-testid={`${testId}-success`}
            className="ps-text-body-v2-xs"
            style={{ color: color }}
          >
            {successMessage}
          </small>
        )}
      </div>
      <button
        onClick={() => onDelete(file.id)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <Icon
          data-testid={`${testId}-delete`}
          size={22}
          icon="delete"
          color={getIconColorByStatus()}
        />
      </button>
    </div>
  );
};
